import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Menu, Plus, Navigation, Search, ArrowLeft } from "lucide-react";

import { DataTable } from "@/components/ui/table";
import { SelectInput } from "../components/FormControls";
import ColumnVisibilityDropdown from "@/components/common/ColumnVisibilityDropdown";
import TablePagination from "@/components/common/TablePagination";
import { callLoader } from "@/redux/Loader/actions";
import { getNavigations } from "@/redux/features/navigation/navigationThunk";
import { getNavigationColumns } from "../components/columns/NavigationColumns";
import AddNavigationModal from "../modals/AddNavigationModal";

function NavigationPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { baseNavigationId } = useParams();

  const accountTypeId = searchParams.get("accountTypeId") || "";

  const { loading, navigationList, pagination } = useSelector(
    (state) => state.navigations,
  );

  const accountTypes = useSelector((state) => state.accountType.accountTypes);
  const baseNavigations = useSelector(
    (state) => state.baseNavigations.baseNavigationlist,
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedNavigation, setSelectedNavigation] = useState("");
  const [selectedAccountType, setSelectedAccountType] = useState("");

  // ==========================================
  // HANDLERS
  // ==========================================

  const handleClose = (open) => {
    setModalOpen(open);

    if (!open) {
      setSelectedNavigation(null);
      setIsEdit(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleAdd = () => {
    setIsEdit(false);
    setSelectedNavigation(null);
    setModalOpen(true);
  };

  const handleView = useCallback(
    (row) => {
      navigate(
        `/admin/base_navigations/${row.BaseNavigationId}/navigations/${row.NavigationId}/sub_navigations?accountTypeId=${accountTypeId}`,
      );
    },
    [navigate, accountTypeId],
  );

  const handleEdit = useCallback((row) => {
    setIsEdit(true);
    setSelectedNavigation(row);
    setModalOpen(true);
  }, []);

  const handleDelete = useCallback(async (row) => {
    try {
      console.log("Delete Navigation:", row);

      // Example:
      // await dispatch(
      //   deleteBaseNavigation(row.BaseNavigationId)
      // ).unwrap();

      // toast.success("Navigation deleted");
    } catch (error) {
      console.error(error);

      // toast.error("Failed to delete navigation");
    }
  }, []);

  // ==========================================
  // COLUMNS
  // ==========================================

  const columns = useMemo(
    () =>
      getNavigationColumns({
        onView: handleView,
        onEdit: handleEdit,
        onDelete: handleDelete,
      }),
    [handleView, handleEdit, handleDelete],
  );
  console.log("Columns", columns);

  const [visibleColumns, setVisibleColumns] = useState(() =>
    columns.map((col) => col.key),
  );

  const filteredColumns = useMemo(
    () =>
      columns.filter(
        (column) =>
          visibleColumns.length === 0 || visibleColumns.includes(column.key),
      ),
    [columns, visibleColumns],
  );

  // ==========================================
  // FETCH DATA
  // ==========================================

  const fetchData = async () => {
    dispatch(callLoader(true));

    try {
      await dispatch(
        getNavigations({
          iPageNo: currentPage,
          iPageRecords: pageSize,
          SearchKeyword: debouncedSearch,
          AccountTypeId: accountTypeId,
          BaseNavigationId: baseNavigationId,
        }),
      );
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(callLoader(false));
    }
  };
  useEffect(() => {
    fetchData();
  }, [
    dispatch,
    currentPage,
    pageSize,
    debouncedSearch,
    accountTypeId,
    baseNavigationId,
  ]);

  // ==========================================
  // SEARCH DEBOUNCE
  // ==========================================

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchTerm.trim();

      if (trimmed === "" || trimmed.length >= 2) {
        setDebouncedSearch(trimmed);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleAccountTypeChange = (name, value) => {
    setSelectedAccountType(value);

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("accountTypeId", value);
      return params;
    });
  };

  const handleBaseNavigationChange = (name, value) => {
    navigate(
      `/admin/base_navigations/${value}/navigations?accountTypeId=${accountTypeId || ""}`,
      { replace: true },
    );
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900 p-6">
      <div className="space-y-4">
        {/* HEADER */}
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <button
              onClick={() => navigate("/admin/base_navigations")}
              className="mt-1 p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Go Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Navigation className="w-7 h-7 text-cyan-500" />
                Navigations
              </h1>

              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Manage navigation links, account type mappings, URLs, and
                display order
              </p>
            </div>
          </div>
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus onClick={handleAdd} className="w-4 h-4" />
            Add Navigation
          </button>
        </div>
        {/* SEARCH & COLUMNS VISIBILITY */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative w-full md:w-96">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                placeholder="Search base module..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
              <span className="font-bold text-slate-900 dark:text-white">
                {pagination?.totalRecord ?? 0} module found
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            <SelectInput
              name="accountTypes"
              value={selectedAccountType}
              onValueChange={handleAccountTypeChange}
              placeholder="All Account Type"
              options={
                accountTypes?.map((item) => ({
                  value: item.VALUE,
                  label: item.TEXT,
                })) || []
              }
            />

            <SelectInput
              name="baseNavigation"
              value={baseNavigationId}
              onValueChange={handleBaseNavigationChange}
              placeholder="All Base Navigation"
              options={
                baseNavigations?.map((item) => ({
                  value: item.BaseNavigationId,
                  label: item.BaseNavigationName,
                })) || []
              }
            />

            <ColumnVisibilityDropdown
              columns={columns}
              visibleColumns={visibleColumns}
              setVisibleColumns={setVisibleColumns}
              storageKey="navigations-table-columns"
            />
          </div>
        </div>
        {/* TABLE */}
        <DataTable
          data={navigationList}
          columns={filteredColumns}
          loading={loading}
          emptyText="No Navigation found"
          Icon={Menu}
        />
        {/* PAGINATION */}
        <div className="p-4 pt-0">
          <TablePagination
            currentPage={currentPage}
            totalPages={pagination?.totalPage || 1}
            totalRecords={pagination?.totalRecord || 0}
            currentRecord={pagination?.currentRecord || 0}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
        {/* MODALS */}
        <AddNavigationModal
          open={modalOpen}
          onOpenChange={handleClose}
          onSuccess={fetchData}
          isEdit={isEdit}
          editData={selectedNavigation}
        />
      </div>
    </div>
  );
}

export default NavigationPage;
